import React from 'react';
import { Mail, Info, ExternalLink } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * ContactEditor - Editor component for the contact form block
 * Minimal editor - just heading/description customization
 */
const ContactEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  return (
    <div className="space-y-4">
      {/* Info Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Mail size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Contact Form</h4>
            <p className="text-sm text-gray-400">
              Messages will be saved to your inbox in the profile editor
            </p>
          </div>
        </div>
      </div>

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Get in Touch"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Description (optional)
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Have a question? Send me a message and I'll get back to you!"
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Show Name Field Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Show Name Field</p>
          <p className="text-xs text-gray-500">Ask for sender's name</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.showNameField !== false}
            onChange={(e) => handleChange('showNameField', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Show Subscribe Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Subscribe Option</p>
          <p className="text-xs text-gray-500">Allow visitors to subscribe to updates</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.showSubscribeOption !== false}
            onChange={(e) => handleChange('showSubscribeOption', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Preview Info */}
      <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-200">
            The form includes rate limiting to prevent spam. Visitors can send one message every 5 minutes.
          </p>
        </div>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['contact']}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['contact']}
          theme={theme || {}}
          blockType="contact"
        />
      </>)}

      {/* Manage Contact Link */}
      <a
        href="/dashboard/website/contact"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Contact
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default ContactEditor;
